import useSWR, { SWRResponse } from "swr";

import { loadGeocoder } from "../blocks/GoogleMapsLoader/googleMapsLoader";
import { useNullableNationalAccountInfo } from "../stores/hooks/useNullableNationalAccountInfo";
import { pushNotification } from "../stores/hooks/useToasts";

const geocoderCache = new Map<string, google.maps.GeocoderResult>();

export type GeocodingResults = google.maps.GeocoderResult | null;

const GEOCODE_TIMEOUT_MS = 5000;

const fetchResults = async (
  location: string,
  country?: string
): Promise<GeocodingResults> => {
  const geocode = new Promise<GeocodingResults>((resolve, reject) => {
    const geocoder = new window.google.maps.Geocoder();

    const request: google.maps.GeocoderRequest = {
      address: location,
      componentRestrictions: { country },
    };

    geocoder.geocode(request, (results, status) => {
      if (status !== google.maps.GeocoderStatus.OK) {
        return reject(new Error(`Geocoding failed: ${status}`));
      }
      const suggested = results?.[0] ?? null;

      if (suggested) {
        geocoderCache.set(location, suggested);
      }

      resolve(suggested);
    });
  });

  const timeout = new Promise<never>((_, reject) => {
    setTimeout(
      () => reject(new Error("Geocoding timed out")),
      GEOCODE_TIMEOUT_MS
    );
  });

  return Promise.race([geocode, timeout]);
};

const geocodeAddress = async (
  location: string,
  country?: string
): Promise<GeocodingResults> => {
  const cached = geocoderCache.get(location);

  if (cached) {
    return cached;
  }

  try {
    await loadGeocoder();

    const results = await fetchResults(location, country);

    if (results) {
      geocoderCache.set(location, results);
    }

    return results;
  } catch {
    pushNotification({ message: "toast.mapsUnavailable" });
    return null;
  }
};

export const useGeocoder = (
  criteria: string
): SWRResponse<GeocodingResults> => {
  const nationalCountryCode =
    useNullableNationalAccountInfo()?.nationalCountryCode;
  return useSWR(criteria || null, () =>
    geocodeAddress(criteria, nationalCountryCode)
  );
};

/**
 * @see docs/worklog/TW-894-hyundai-national-not-loading/rfc.md
 * @see docs/worklog/TW-894-hyundai-national-not-loading/tfc.md
 */
